import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Button } from '~/components/common/Button'
import {
  MonthCalendar,
  isDateInSelectableRange,
  isSameDay
} from '~/components/common/MonthCalendar'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { parseApiDate } from '~/utils/timezone'
import { cm } from '~/utils/utils'
import { Typography } from '../common/Typography'

// ============================================================================
// Icons (only the ones still used by the panel chrome — month-grid icons live
// inside MonthCalendar.)
// ============================================================================

const ArrowLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <path
      d='M19 12H5M5 12L12 19M5 12L12 5'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <path
      d='M15 18L9 12L15 6'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <path
      d='M9 18L15 12L9 6'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const SwapRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <path
      d='M16 12H8M16 12L13 9M16 12L13 15'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <rect x='3' y='4' width='18' height='18' rx='2' stroke='currentColor' strokeWidth='1.5' />
    <path d='M16 2V6M8 2V6M3 10H21' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
  </svg>
)

// ============================================================================
// Date helpers (panel-specific helpers; primitive grid + selection helpers live
// in MonthCalendar.)
// ============================================================================

const MONTHS_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

function addCalendarMonths(
  year: number,
  month: number,
  delta: number
): { year: number; month: number } {
  const d = new Date(year, month + delta, 1)
  return { year: d.getFullYear(), month: d.getMonth() }
}

function areAdjacentMonths(leftY: number, leftM: number, rightY: number, rightM: number): boolean {
  const n = addCalendarMonths(leftY, leftM, 1)
  return n.year === rightY && n.month === rightM
}

function initialYmdFromProp(str?: string): string {
  if (!str) return ''
  const m = str.match(/^(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : ''
}

/** True only for strict YYYY-MM-DD that parses to a real calendar day. */
function isValidYmd(s: string): boolean {
  if (!s || s.length !== 10) return false
  const d = parseApiDate(s)
  return d != null && !isNaN(d.getTime())
}

/** Valid shape and within inclusive YYYY-MM-DD bounds (when provided). */
function isSelectableYmd(
  s: string,
  minYmd: string | undefined,
  maxYmd: string | undefined
): boolean {
  if (!isValidYmd(s)) return false
  if (minYmd && s < minYmd) return false
  if (maxYmd && s > maxYmd) return false
  return true
}

function toApiDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// ============================================================================
// Types
// ============================================================================

type SelectionFocus = 'start' | 'end'
type CalendarView = 'days' | 'months' | 'years'

interface DateRangePanelProps {
  /** Initial start date (YYYY-MM-DD string or null) */
  initialStartDate?: string
  /** Initial end date (YYYY-MM-DD string or null) */
  initialEndDate?: string
  /** Inclusive min calendar day (YYYY-MM-DD), e.g. Unix epoch day for usage charts */
  minSelectableDate?: string
  /** Inclusive max calendar day (YYYY-MM-DD), e.g. today */
  maxSelectableDate?: string
  /** Called when a complete range is selected */
  onRangeSelect: (startDate: string, endDate: string) => void
  /** Called when user clicks "Back" */
  onBack: () => void
}

// ============================================================================
// Component
// ============================================================================

export const DateRangePanel: React.FC<DateRangePanelProps> = ({
  initialStartDate,
  initialEndDate,
  minSelectableDate,
  maxSelectableDate,
  onRangeSelect,
  onBack
}) => {
  const anchorFromInitial = (): Date => {
    const ymd = initialYmdFromProp(initialStartDate)
    if (ymd && isValidYmd(ymd)) {
      const d = parseApiDate(ymd)
      if (d) return d
    }
    return new Date()
  }

  const [startDraft, setStartDraft] = useState(() => initialYmdFromProp(initialStartDate))
  const [endDraft, setEndDraft] = useState(() => initialYmdFromProp(initialEndDate))
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null)
  const [hoveredSide, setHoveredSide] = useState<'left' | 'right' | 'single' | null>(null)
  const [focus, setFocus] = useState<SelectionFocus>('start')
  /** Which date type has been "unhooked" from its native panel, allowing the opposite panel to set it. */
  const [unhooked, setUnhooked] = useState<'start' | 'end' | null>(null)
  const isCompactViewport = useCompactViewport()

  /** Native date inputs: store raw YYYY-MM-DD like super-admin license fields — no clamping on change; invalid shows error styling. */
  const startInputRef = useRef<HTMLInputElement>(null)
  const endInputRef = useRef<HTMLInputElement>(null)

  const startDate = useMemo(
    () => (isValidYmd(startDraft) ? parseApiDate(startDraft) : null),
    [startDraft]
  )
  const endDate = useMemo(() => (isValidYmd(endDraft) ? parseApiDate(endDraft) : null), [endDraft])

  // Calendar navigation — two independent months; coupled nav shifts the pair as adjacent months.
  const [leftYear, setLeftYear] = useState(() => anchorFromInitial().getFullYear())
  const [leftMonth, setLeftMonth] = useState(() => anchorFromInitial().getMonth())
  const [rightYear, setRightYear] = useState(() => {
    const a = anchorFromInitial()
    const r = addCalendarMonths(a.getFullYear(), a.getMonth(), 1)
    return r.year
  })
  const [rightMonth, setRightMonth] = useState(() => {
    const a = anchorFromInitial()
    const r = addCalendarMonths(a.getFullYear(), a.getMonth(), 1)
    return r.month
  })

  // Month/year picker state — which panel's header was clicked
  const [calendarView, setCalendarView] = useState<CalendarView>('days')
  const [pickerSide, setPickerSide] = useState<'left' | 'right'>('left')
  const [yearPageStart, setYearPageStart] = useState(() => anchorFromInitial().getFullYear() - 4)

  const startInvalid =
    startDraft !== '' && !isSelectableYmd(startDraft, minSelectableDate, maxSelectableDate)
  const endInvalid =
    endDraft !== '' &&
    (!isSelectableYmd(endDraft, minSelectableDate, maxSelectableDate) ||
      (isValidYmd(startDraft) && isValidYmd(endDraft) && endDraft < startDraft))

  /**
   * Like super-admin license Save: do not call `onRangeSelect` until the range is fully valid.
   * Draft strings are not rewritten when out of range — border shows invalid (same idea as native datetime-local).
   */
  const canApplyRange = useMemo(
    () =>
      isSelectableYmd(startDraft, minSelectableDate, maxSelectableDate) &&
      isSelectableYmd(endDraft, minSelectableDate, maxSelectableDate) &&
      endDraft >= startDraft,
    [startDraft, endDraft, minSelectableDate, maxSelectableDate]
  )

  const handleApplyRange = useCallback(() => {
    if (!canApplyRange) return
    onRangeSelect(startDraft, endDraft)
  }, [canApplyRange, startDraft, endDraft, onRangeSelect])

  const showIncompleteRangeHint = !canApplyRange && (startDraft !== '' || endDraft !== '')

  /**
   * Dual calendar: each panel has its own prev/next. If the two months are adjacent (right === left + 1),
   * left-panel forward and right-panel back move both calendars together; other buttons move only that panel.
   * Compact: single month uses left panel handlers only.
   */
  const shiftCoupledBackOneMonth = useCallback(() => {
    const nl = addCalendarMonths(leftYear, leftMonth, -1)
    setLeftYear(nl.year)
    setLeftMonth(nl.month)
    const nr = addCalendarMonths(nl.year, nl.month, 1)
    setRightYear(nr.year)
    setRightMonth(nr.month)
  }, [leftYear, leftMonth])

  const shiftCoupledForwardOneMonth = useCallback(() => {
    const nr = addCalendarMonths(rightYear, rightMonth, 1)
    setRightYear(nr.year)
    setRightMonth(nr.month)
    const nl = addCalendarMonths(nr.year, nr.month, -1)
    setLeftYear(nl.year)
    setLeftMonth(nl.month)
  }, [rightYear, rightMonth])

  const monthsAdjacent = useMemo(
    () => areAdjacentMonths(leftYear, leftMonth, rightYear, rightMonth),
    [leftYear, leftMonth, rightYear, rightMonth]
  )

  const leftPanelPrevMonth = useCallback(() => {
    if (isCompactViewport) {
      if (leftMonth === 0) {
        setLeftYear(y => y - 1)
        setLeftMonth(11)
      } else {
        setLeftMonth(m => m - 1)
      }
      return
    }
    const nl = addCalendarMonths(leftYear, leftMonth, -1)
    setLeftYear(nl.year)
    setLeftMonth(nl.month)
  }, [isCompactViewport, leftYear, leftMonth])

  const leftPanelNextMonth = useCallback(() => {
    if (isCompactViewport) {
      if (leftMonth === 11) {
        setLeftYear(y => y + 1)
        setLeftMonth(0)
      } else {
        setLeftMonth(m => m + 1)
      }
      return
    }
    if (monthsAdjacent) {
      shiftCoupledForwardOneMonth()
      return
    }
    const nl = addCalendarMonths(leftYear, leftMonth, 1)
    setLeftYear(nl.year)
    setLeftMonth(nl.month)
  }, [isCompactViewport, monthsAdjacent, leftYear, leftMonth, shiftCoupledForwardOneMonth])

  const rightPanelPrevMonth = useCallback(() => {
    if (isCompactViewport) return
    if (monthsAdjacent) {
      shiftCoupledBackOneMonth()
      return
    }
    const nr = addCalendarMonths(rightYear, rightMonth, -1)
    setRightYear(nr.year)
    setRightMonth(nr.month)
  }, [isCompactViewport, monthsAdjacent, rightYear, rightMonth, shiftCoupledBackOneMonth])

  const rightPanelNextMonth = useCallback(() => {
    if (isCompactViewport) return
    const nr = addCalendarMonths(rightYear, rightMonth, 1)
    setRightYear(nr.year)
    setRightMonth(nr.month)
  }, [isCompactViewport, rightYear, rightMonth])

  const leftPanelPrevYear = useCallback(() => {
    if (isCompactViewport) {
      setLeftYear(y => y - 1)
      return
    }
    const nl = addCalendarMonths(leftYear, leftMonth, -12)
    setLeftYear(nl.year)
    setLeftMonth(nl.month)
  }, [isCompactViewport, leftYear, leftMonth])

  const leftPanelNextYear = useCallback(() => {
    if (isCompactViewport) {
      setLeftYear(y => y + 1)
      return
    }
    if (monthsAdjacent) {
      const nr = addCalendarMonths(rightYear, rightMonth, 12)
      setRightYear(nr.year)
      setRightMonth(nr.month)
      const nl = addCalendarMonths(nr.year, nr.month, -1)
      setLeftYear(nl.year)
      setLeftMonth(nl.month)
      return
    }
    const nl = addCalendarMonths(leftYear, leftMonth, 12)
    setLeftYear(nl.year)
    setLeftMonth(nl.month)
  }, [isCompactViewport, monthsAdjacent, leftYear, leftMonth, rightYear, rightMonth])

  const rightPanelPrevYear = useCallback(() => {
    if (isCompactViewport) return
    if (monthsAdjacent) {
      const nl = addCalendarMonths(leftYear, leftMonth, -12)
      setLeftYear(nl.year)
      setLeftMonth(nl.month)
      const nr = addCalendarMonths(nl.year, nl.month, 1)
      setRightYear(nr.year)
      setRightMonth(nr.month)
      return
    }
    const nr = addCalendarMonths(rightYear, rightMonth, -12)
    setRightYear(nr.year)
    setRightMonth(nr.month)
  }, [isCompactViewport, monthsAdjacent, leftYear, leftMonth, rightYear, rightMonth])

  const rightPanelNextYear = useCallback(() => {
    if (isCompactViewport) return
    const nr = addCalendarMonths(rightYear, rightMonth, 12)
    setRightYear(nr.year)
    setRightMonth(nr.month)
  }, [isCompactViewport, rightYear, rightMonth])

  // Date click handler — left panel sets start, right panel sets end.
  // Clicking a selected start/end date on ANY panel unhooks it.
  // While unhooked, clicking the remaining selected date does nothing.
  const handleDateClick = useCallback(
    (date: Date, side: 'left' | 'right' | 'single') => {
      if (!isDateInSelectableRange(date, minSelectableDate, maxSelectableDate)) return

      if (side === 'single') {
        if (focus === 'start') {
          setStartDraft(toApiDateString(date))
          setEndDraft('')
          setFocus('end')
        } else {
          if (startDate && date.getTime() < startDate.getTime()) {
            setStartDraft(toApiDateString(date))
            setEndDraft('')
            setFocus('end')
          } else {
            setEndDraft(toApiDateString(date))
            setFocus('start')
          }
        }
        return
      }

      // Clicking a selected start date on either panel → unhook start (only when nothing else is unhooked)
      if (startDate && isSameDay(date, startDate)) {
        if (unhooked !== null) return
        setStartDraft('')
        setUnhooked('start')
        return
      }

      // Clicking a selected end date on either panel → unhook end (only when nothing else is unhooked)
      if (endDate && isSameDay(date, endDate)) {
        if (unhooked !== null) return
        setEndDraft('')
        setUnhooked('end')
        return
      }

      const ymd = toApiDateString(date)

      // Determine which date type this click sets based on panel + unhooked state
      const setsStart =
        (side === 'left' && unhooked !== 'end') || (side === 'right' && unhooked === 'start')

      if (setsStart) {
        if (endDate && date.getTime() > endDate.getTime()) {
          // Picked start after end → swap: old end becomes start, picked becomes end
          setStartDraft(endDraft)
          setEndDraft(ymd)
        } else {
          setStartDraft(ymd)
        }
        setUnhooked(null)
        return
      }

      // Sets end
      if (startDate && date.getTime() < startDate.getTime()) {
        // Picked end before start → swap: old start becomes end, picked becomes start
        setEndDraft(startDraft)
        setStartDraft(ymd)
      } else {
        setEndDraft(ymd)
      }
      setUnhooked(null)
    },
    [focus, startDate, endDate, unhooked, minSelectableDate, maxSelectableDate]
  )

  const isStartHover =
    hoveredDate != null &&
    ((hoveredSide === 'left' && unhooked !== 'end') ||
      (hoveredSide === 'right' && unhooked === 'start') ||
      (hoveredSide === 'single' && focus === 'start'))
  const isEndHover =
    hoveredDate != null &&
    ((hoveredSide === 'right' && unhooked !== 'start') ||
      (hoveredSide === 'left' && unhooked === 'end') ||
      (hoveredSide === 'single' && focus === 'end'))

  const showStartPreview =
    isStartHover && endDate != null && hoveredDate!.getTime() < endDate.getTime()
  const previewStart = showStartPreview ? hoveredDate : startDate

  const showEndPreview =
    isEndHover && startDate != null && hoveredDate!.getTime() > startDate.getTime()
  const previewEnd = showEndPreview ? hoveredDate : endDate

  // Input focus handlers
  const handleStartInputFocus = useCallback(() => setFocus('start'), [])
  const handleEndInputFocus = useCallback(() => {
    if (startDate) setFocus('end')
  }, [startDate])

  const handleStartDateNativeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value
      setStartDraft(v)
      if (v && isValidYmd(v) && isValidYmd(endDraft) && v > endDraft) {
        setEndDraft('')
      }
      if (v && isValidYmd(v)) {
        const parsed = parseApiDate(v)!
        setLeftYear(parsed.getFullYear())
        setLeftMonth(parsed.getMonth())
        if (!isValidYmd(endDraft) || endDraft < v) {
          const r = addCalendarMonths(parsed.getFullYear(), parsed.getMonth(), 1)
          setRightYear(r.year)
          setRightMonth(r.month)
        }
      }
    },
    [endDraft]
  )

  const handleEndDateNativeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDraft(e.target.value)
  }, [])

  const handleStartInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      endInputRef.current?.focus()
    }
  }, [])

  const handleEndInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') e.preventDefault()
  }, [])

  // Month/year picker handlers
  const handleMonthYearClick = useCallback(
    (side: 'left' | 'right') => {
      setPickerSide(side)
      setCalendarView(prev => (prev === 'days' ? 'months' : 'days'))
      const yr = side === 'left' ? leftYear : rightYear
      setYearPageStart(yr - 4)
    },
    [leftYear, rightYear]
  )

  const handleMonthSelect = useCallback(
    (monthIndex: number) => {
      if (pickerSide === 'left') {
        setLeftMonth(monthIndex)
      } else {
        setRightMonth(monthIndex)
      }
      setCalendarView('days')
    },
    [pickerSide]
  )

  const handleYearSelect = useCallback(
    (year: number) => {
      if (pickerSide === 'left') {
        setLeftYear(year)
      } else {
        setRightYear(year)
      }
      setCalendarView('months')
    },
    [pickerSide]
  )

  // Render month picker grid
  const renderMonthPicker = () => {
    const activeYear = pickerSide === 'left' ? leftYear : rightYear
    const activeMonth = pickerSide === 'left' ? leftMonth : rightMonth

    return (
      <div className='flex flex-col w-[560px]'>
        <div className='flex items-center justify-between px-4 py-[9px] border-b border-tokens-neutral-fill-fill-6'>
          <button
            type='button'
            onClick={() => {
              if (pickerSide === 'left') {
                setLeftYear(activeYear - 1)
                setLeftMonth(activeMonth)
              } else {
                setRightYear(activeYear - 1)
                setRightMonth(activeMonth)
              }
            }}
            className='size-6 flex items-center justify-center hover:bg-tokens-neutral-fill-fill-4 rounded cursor-pointer transition-colors'
          >
            <ChevronLeftIcon className='size-4 text-tokens-neutral-text-quaternary' />
          </button>
          <button
            onClick={() => {
              setCalendarView('years')
              setYearPageStart(activeYear - 4)
            }}
            className='hover:bg-tokens-neutral-fill-fill-4 px-2 py-0.5 rounded cursor-pointer transition-colors'
          >
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='semibold'
              className='text-tokens-neutral-text-primary'
              style={{ fontSize: '14px', lineHeight: '22px' }}
            >
              {activeYear}
            </Typography>
          </button>
          <button
            type='button'
            onClick={() => {
              if (pickerSide === 'left') {
                setLeftYear(activeYear + 1)
                setLeftMonth(activeMonth)
              } else {
                setRightYear(activeYear + 1)
                setRightMonth(activeMonth)
              }
            }}
            className='size-6 flex items-center justify-center hover:bg-tokens-neutral-fill-fill-4 rounded cursor-pointer transition-colors'
          >
            <ChevronRightIcon className='size-4 text-tokens-neutral-text-quaternary' />
          </button>
        </div>
        <div className='grid grid-cols-3 gap-2 p-4'>
          {MONTHS_FULL.map((name, idx) => (
            <button
              key={name}
              onClick={() => handleMonthSelect(idx)}
              className={`h-[40px] rounded-[8px] text-[14px] cursor-pointer transition-colors ${
                idx === activeMonth
                  ? 'bg-tokens-status-link-1 text-white font-medium'
                  : 'hover:bg-tokens-neutral-fill-fill-4 text-tokens-neutral-text-primary'
              }`}
              style={{ fontFamily: 'Geist, sans-serif' }}
            >
              {name}
            </button>
          ))}
        </div>
      </div>
    )
  }

  // Render year picker grid
  const renderYearPicker = () => {
    const activeYear = pickerSide === 'left' ? leftYear : rightYear
    const years: number[] = []
    for (let i = 0; i < 12; i++) years.push(yearPageStart + i)

    return (
      <div className='flex flex-col w-[560px]'>
        <div className='flex items-center justify-between px-4 py-[9px] border-b border-tokens-neutral-fill-fill-6'>
          <button
            onClick={() => setYearPageStart(y => y - 12)}
            className='size-6 flex items-center justify-center hover:bg-tokens-neutral-fill-fill-4 rounded cursor-pointer transition-colors'
          >
            <ChevronLeftIcon className='size-4 text-tokens-neutral-text-quaternary' />
          </button>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            fontWeight='semibold'
            className='text-tokens-neutral-text-primary'
            style={{ fontSize: '14px', lineHeight: '22px' }}
          >
            {yearPageStart} – {yearPageStart + 11}
          </Typography>
          <button
            onClick={() => setYearPageStart(y => y + 12)}
            className='size-6 flex items-center justify-center hover:bg-tokens-neutral-fill-fill-4 rounded cursor-pointer transition-colors'
          >
            <ChevronRightIcon className='size-4 text-tokens-neutral-text-quaternary' />
          </button>
        </div>
        <div className='grid grid-cols-3 gap-2 p-4'>
          {years.map(yr => (
            <button
              key={yr}
              onClick={() => handleYearSelect(yr)}
              className={`h-[40px] rounded-[8px] text-[14px] cursor-pointer transition-colors ${
                yr === activeYear
                  ? 'bg-tokens-status-link-1 text-white font-medium'
                  : 'hover:bg-tokens-neutral-fill-fill-4 text-tokens-neutral-text-primary'
              }`}
              style={{ fontFamily: 'Geist, sans-serif' }}
            >
              {yr}
            </button>
          ))}
        </div>
      </div>
    )
  }

  /**
   * Render a single month panel. Wraps {@link MonthCalendar} with the
   * panel-specific navigation handlers and side-aware hover/click wiring.
   */
  const renderMonth = (year: number, month: number, side: 'left' | 'right' | 'single') => {
    const isLeftish = side === 'left' || side === 'single'
    return (
      <MonthCalendar
        year={year}
        month={month}
        selectedStart={startDate}
        selectedEnd={endDate}
        previewStart={previewStart}
        previewEnd={previewEnd}
        minSelectableDate={minSelectableDate}
        maxSelectableDate={maxSelectableDate}
        onDateClick={date => handleDateClick(date, side)}
        onDateHover={date => {
          setHoveredDate(date)
          setHoveredSide(side)
        }}
        onDateLeave={() => {
          setHoveredDate(null)
          setHoveredSide(null)
        }}
        onPrevYear={
          isCompactViewport ? undefined : isLeftish ? leftPanelPrevYear : rightPanelPrevYear
        }
        onPrevMonth={isLeftish ? leftPanelPrevMonth : rightPanelPrevMonth}
        onNextMonth={side === 'right' ? rightPanelNextMonth : leftPanelNextMonth}
        onNextYear={
          isCompactViewport ? undefined : side === 'right' ? rightPanelNextYear : leftPanelNextYear
        }
        onMonthYearClick={() => handleMonthYearClick(side === 'single' ? 'left' : side)}
        compact={isCompactViewport}
        panelTestId={`calendar-panel-${side}`}
      />
    )
  }

  return (
    <div className='flex flex-col gap-[11px] pt-2 pb-4 px-4 min-w-0 max-w-full'>
      {/* Back button */}
      <button
        onClick={onBack}
        className='h-8 px-3 flex items-center gap-2 rounded-[8px] hover:bg-tokens-neutral-fill-fill-4 cursor-pointer transition-colors self-start'
      >
        <ArrowLeftIcon className='size-5 text-tokens-status-link-1' />
        <Typography
          fontFamily='geist'
          fontSize='bodyMd'
          fontWeight='medium'
          className='text-tokens-status-link-1'
          style={{ fontSize: '14px', lineHeight: '22px' }}
        >
          Back
        </Typography>
      </button>

      {/* Date range: native `type="date"` + draft strings — invalid/out-of-range shows error border (super-admin license pattern). */}
      <div
        className={cm(
          'flex w-full min-w-0 max-w-full items-center gap-2 min-h-8 px-3 py-1 border rounded-[8px] bg-white overflow-hidden',
          startInvalid || endInvalid
            ? 'border-tokens-status-error-1'
            : 'border-tokens-neutral-border-1'
        )}
      >
        <input
          ref={startInputRef}
          type='date'
          data-testid='usage-date-range-start'
          aria-label='Usage range start date'
          aria-invalid={startInvalid}
          value={startDraft}
          onChange={handleStartDateNativeChange}
          onFocus={handleStartInputFocus}
          onKeyDown={handleStartInputKeyDown}
          className={`min-w-0 flex-1 basis-0 shrink text-[14px] leading-[22px] bg-transparent outline-none [color-scheme:light] ${
            focus === 'start'
              ? 'text-tokens-neutral-text-primary font-medium'
              : startDraft
                ? 'text-tokens-neutral-text-primary'
                : 'text-tokens-neutral-text-placeholder'
          }`}
          style={{ fontFamily: 'Geist, sans-serif' }}
        />
        <SwapRightIcon className='size-5 text-tokens-neutral-text-quaternary flex-shrink-0' />
        <input
          ref={endInputRef}
          type='date'
          data-testid='usage-date-range-end'
          aria-label='Usage range end date'
          aria-invalid={endInvalid}
          value={endDraft}
          onChange={handleEndDateNativeChange}
          onFocus={handleEndInputFocus}
          onKeyDown={handleEndInputKeyDown}
          className={`min-w-0 flex-1 basis-0 shrink text-[14px] leading-[22px] bg-transparent outline-none [color-scheme:light] ${
            focus === 'end'
              ? 'text-tokens-neutral-text-primary font-medium'
              : endDraft
                ? 'text-tokens-neutral-text-primary'
                : 'text-tokens-neutral-text-placeholder'
          }`}
          style={{ fontFamily: 'Geist, sans-serif' }}
        />
        <CalendarIcon className='size-5 text-tokens-neutral-text-quaternary flex-shrink-0' />
      </div>

      {/* Calendar area — switches between day grid, month picker, and year picker */}
      <div className='flex min-w-0 max-w-full rounded-[6px] overflow-hidden'>
        {calendarView === 'days' &&
          (isCompactViewport ? (
            renderMonth(leftYear, leftMonth, 'single')
          ) : (
            <>
              {renderMonth(leftYear, leftMonth, 'left')}
              {renderMonth(rightYear, rightMonth, 'right')}
            </>
          ))}
        {calendarView === 'months' && renderMonthPicker()}
        {calendarView === 'years' && renderYearPicker()}
      </div>

      <div className='flex min-w-0 max-w-full flex-col gap-2 px-4 pt-3 pb-1 border-t border-tokens-neutral-fill-fill-6'>
        {showIncompleteRangeHint && (
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            fontWeight='normal'
            className='text-tokens-neutral-text-secondary break-words min-w-0'
            style={{ fontSize: '12px', lineHeight: '18px' }}
          >
            Choose a valid range within allowed dates (end on or after start). Invalid fields are
            outlined in red; Apply stays disabled until the range is valid.
          </Typography>
        )}
        <div className='flex justify-end'>
          <Button
            type='button'
            variant='primary'
            isDisabled={!canApplyRange}
            onClick={handleApplyRange}
            className='h-9 px-4 text-sm min-w-[88px]'
            data-testid='usage-date-range-apply'
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DateRangePanel
